/**
 * @file DepartmentMultiSelect.js
 * @description A multi-select dropdown component for selecting departments.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchDepartments } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * DepartmentMultiSelect component.
 *
 * @component
 * @param {string} className - The class name for the component.
 * @param {Array} selectedDepartments - The array of selected departments.
 * @param {Function} onDepartmentsChange - The function to handle department selection change.
 * @param {string} [label="Departments"] - The label for the component.
 * @param {boolean} [isRequired=false] - Indicates if the component is required.
 * @param {boolean} resetTrigger - The trigger to reset the component.
 * @param {string} tenantId - The ID of the tenant.
 * @param {boolean} [showSelectedItemsInField=true] - Indicates if the selected items should be shown in the field.
 * @param {boolean} [showSelectionBox=true] - Indicates if the selection box should be shown.
 * @returns {JSX.Element} The rendered DepartmentMultiSelect component.
 */
const DepartmentMultiSelect = ({
  className,
  selectedDepartments,
  onDepartmentsChange,
  label = "Departments",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setIsLoading(true);
        const data = await fetchDepartments(tenantId);
        setDepartments(data);
      } catch (error) {
        console.error("Could not fetch departments:", error);
        Toast.error(getText('msg_951289f71b89'));
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartments();
  }, [tenantId]);

  const handleDepartmentsChange = (selectedOptions) => {
    onDepartmentsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="department-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="department-dropdown"
        options={departments}
        optionIDField="id"
        updateOptions={handleDepartmentsChange}
        savedOptions={selectedDepartments}
        savedOptionIDField="id"
        disabled={departments.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_0c727f1d35cd')}
      />
    </div>
  );
};

export default DepartmentMultiSelect;
